import { useEffect } from 'react'
import { t } from 'i18next'
import { startProgressWatch } from '@client/stores/progressWatch'
import { toastError } from '@client/utils/toastMessage'
import { cancelOwnConversions, findActiveConversionsOf, knownActiveConversionsOf } from './activeConversions'
import type { TConversionsAnswer } from './useConversionsQuestion'

//
// F5 with one's own conversions waiting or running (operator, 17.10.2026, F3).
//
// Before the page goes the browser allows only its own notice, no question of
// ours. So the notice is asked for, a mark is left in sessionStorage as the
// page goes, and the reloaded app asks once — as long as something of one's
// own still waits or runs. Without an answer it runs on.
//

const RELOAD_MARK = 'msbc-convert-reload'

const writeMark = (on: boolean) => {
    try {
        if (on) sessionStorage.setItem(RELOAD_MARK, '1')
        else sessionStorage.removeItem(RELOAD_MARK)
    } catch {
        // without storage there is no question after the reload; the browser's notice still came
    }
}

const readMark = () => {
    try {
        return sessionStorage.getItem(RELOAD_MARK) === '1'
    } catch {
        return false
    }
}

export default function useConversionsOnReload(userId: string, ask: (message: string) => Promise<TConversionsAnswer>) {
    // the store has to hear the events to know at unload time what still runs
    useEffect(() => startProgressWatch(), [])

    useEffect(() => {
        if (!userId) return

        const onBeforeUnload = (event: BeforeUnloadEvent) => {
            if (!knownActiveConversionsOf(userId).length) return

            event.preventDefault()
            // older browsers want the property set as well
            event.returnValue = ''
        }

        // Only once the page really goes: whoever stays after the notice
        // must not be asked by a later reload.
        const onPageHide = () => {
            if (knownActiveConversionsOf(userId).length) writeMark(true)
        }

        window.addEventListener('beforeunload', onBeforeUnload)
        window.addEventListener('pagehide', onPageHide)
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload)
            window.removeEventListener('pagehide', onPageHide)
        }
    }, [userId])

    // No cleanup that drops the question: StrictMode runs the effect twice, the
    // first run has taken the mark, and a dropped first run asked nobody.
    useEffect(() => {
        if (!userId || !readMark()) return
        writeMark(false)

        void (async () => {
            const own = await findActiveConversionsOf(userId)
            if (!own.length) return

            const answer = await ask(t('Your conversions are still waiting or running. Cancel them or let them run?'))
            if (answer !== 'cancel') return

            for (const reason of await cancelOwnConversions(own)) toastError(reason)
        })()
    }, [userId])
}
